// ─────────────────────────────────────────────────────────────────
// Glass-i — Purchase Return Register page (Stage 6P.8).
//
// Server-paginated read-only register at
// /erp/purchase-returns/register.
//
// Columns (brief §K3):
//   Return No · Supplier (+ GSTIN tag) · Bill No · Debit Note No
//   Taxable · GST · Grand Total · Inventory Posted (Tag) ·
//   Accounting Voucher · Status (Tag)
//
// Toolbar:
//   Date range (return_date) · Supplier search · Clear · Refresh
//
// Row interactions:
//   • Double-click → navigate to /erp/purchase-returns/:id
//   • Return No    → same navigation
//
// Backend remains the authority for ALL return math:
//   return_no allocation, voucher posting, inventory ledger
//   idempotency, ITC reversal. This page is a pure read surface —
//   it never mutates returns and never computes balances locally.
//
// Style:
//   Mirrors SupplierPaymentRegisterPage — slate-50 header strip,
//   22 px row density, sticky toolbar. Reuses the shared
//   GoodsReceiptsPage.css via the .grn-* wrapper so no new CSS file
//   is added.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, DatePicker, Input,
  Space, Spin, Table, Tag, Tooltip, message
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, FileDoneOutlined
} from '@ant-design/icons';

import { purchaseReturnApi } from './purchaseReturnApi';
import {
  returnStatusLabel,
  returnStatusColor
} from './purchaseReturnHelpers';
import useAuth from '../../../hooks/useAuth';
import '../purchase/GoodsReceiptsPage.css';

const { RangePicker } = DatePicker;


// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS  = ['25', '50', '100'];
const DATE_FMT           = 'DD-MMM-YYYY';
const ISO_FMT            = 'YYYY-MM-DD';
const SEARCH_DEBOUNCE_MS = 250;


// ─────────────────────────────────────────────────────────────────
// Pure helpers — kept local so the page doesn't depend on a shared
// formatter module that could drift.
// ─────────────────────────────────────────────────────────────────

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
}

// ₹ grouping is locale-aware (en-IN) so 12,34,567.89 is rendered the
// same way as elsewhere on the control plane. Falsy / non-finite
// inputs collapse to an em-dash so empty rows don't read as "₹0".
function fmtMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  try {
    return n.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } catch {
    return n.toFixed(2);
  }
}

function pickField(row, snake, camel) {
  if (!row) return undefined;
  if (camel in row && row[camel] !== undefined && row[camel] !== null) return row[camel];
  if (snake in row && row[snake] !== undefined && row[snake] !== null) return row[snake];
  return undefined;
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

// GST total = cgst + sgst + igst. The register endpoint typically
// emits total_tax already; we prefer it when present and derive from
// the parts only as a fallback so the column still reads when the
// API surfaces just the legs.
function readGst(row) {
  if (!row) return null;
  const total = pickField(row, 'total_tax', 'totalTax');
  if (total !== undefined && total !== null && total !== '') {
    const n = Number(total);
    if (Number.isFinite(n)) return n;
  }
  const c = Number(pickField(row, 'cgst_amount', 'cgstAmount') || 0);
  const s = Number(pickField(row, 'sgst_amount', 'sgstAmount') || 0);
  const i = Number(pickField(row, 'igst_amount', 'igstAmount') || 0);
  return (Number.isFinite(c) ? c : 0)
       + (Number.isFinite(s) ? s : 0)
       + (Number.isFinite(i) ? i : 0);
}


// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function PurchaseReturnRegisterPage() {
  const navigate = useNavigate();
  const { me } = useAuth();

  // Wildcard '*' is the master role; explicit
  // `erp.purchase.returns.*` strings are the Stage 6P.8 perms.
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canView        = hasPerm('erp.purchase.returns.view');
  const canViewReports = hasPerm('erp.purchase.returns.reports.view');

  // ── Toolbar state ────────────────────────────────────────────
  const [supplierInput, setSupplierInput] = useState('');
  const [supplier,      setSupplier]      = useState('');
  const [dateRange,     setDateRange]     = useState(null);

  // ── Server pagination state ──────────────────────────────────
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total,    setTotal]    = useState(0);

  // ── Data state ───────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);

  // ── Debounced supplier search ────────────────────────────────
  const supplierDebounceRef = useRef(null);
  useEffect(() => {
    if (supplierDebounceRef.current) clearTimeout(supplierDebounceRef.current);
    supplierDebounceRef.current = setTimeout(() => {
      setSupplier((prev) => {
        if (prev === supplierInput) return prev;
        setPage(1);
        return supplierInput;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (supplierDebounceRef.current) clearTimeout(supplierDebounceRef.current);
    };
  }, [supplierInput]);

  // ── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!canView && !canViewReports) return;
    setLoading(true);
    try {
      const params = { page, pageSize };

      const supplierNeedle = (supplier || '').trim();
      if (supplierNeedle) {
        params.supplier     = supplierNeedle;
        params.supplierName = supplierNeedle;
      }
      if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
        params.fromDate = dateRange[0].format
          ? dateRange[0].format(ISO_FMT)
          : new Date(dateRange[0]).toISOString().slice(0, 10);
        params.toDate   = dateRange[1].format
          ? dateRange[1].format(ISO_FMT)
          : new Date(dateRange[1]).toISOString().slice(0, 10);
      }

      const out = await purchaseReturnApi.listRegister(params);
      const list = Array.isArray(out) ? out
        : Array.isArray(out?.rows) ? out.rows
        : Array.isArray(out?.data) ? out.data
        : Array.isArray(out?.items) ? out.items
        : [];
      const count = Array.isArray(out) ? list.length
        : Number(out?.total ?? out?.count ?? list.length) || 0;

      setRows(list);
      setTotal(count);
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load purchase return register.'),
        key: 'pr-register'
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canView, canViewReports, page, pageSize, supplier, dateRange]);

  useEffect(() => { load(); }, [load]);

  // ── Toolbar handlers — any filter change resets page to 1 ────
  const onDates = (v) => { setDateRange(v || null); setPage(1); };
  const onReset = () => {
    setSupplierInput('');
    setSupplier('');
    setDateRange(null);
    setPage(1);
  };

  // ── Row navigation ───────────────────────────────────────────
  const openView = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    navigate(`/erp/purchase-returns/${encodeURIComponent(id)}`);
  }, [navigate]);

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'Return No',
      dataIndex: 'returnNo',
      width:     180,
      render: (_v, row) => {
        const no = pickField(row, 'return_no', 'returnNo') || '—';
        return (
          <button
            type="button"
            className="grn-link"
            onClick={() => openView(row)}
          >
            {no}
          </button>
        );
      }
    },
    {
      title:     'Return Date',
      dataIndex: 'returnDate',
      width:     120,
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtDate(pickField(row, 'return_date', 'returnDate'))}
        </span>
      )
    },
    {
      title:     'Supplier',
      dataIndex: 'supplierName',
      render: (_v, row) => {
        const name  = pickField(row, 'supplier_name',  'supplierName')  || '—';
        const gstin = pickField(row, 'supplier_gstin', 'supplierGstin');
        return (
          <div className="grn-supplier">
            <Tooltip title={name}>
              <span className="grn-supplier__name">{name}</span>
            </Tooltip>
            {gstin
              ? <Tag color="blue" className="grn-gstin-tag">{gstin}</Tag>
              : null}
          </div>
        );
      }
    },
    {
      title:     'Bill No',
      dataIndex: 'purchaseBillNo',
      width:     160,
      render: (_v, row) => {
        const v = pickField(row, 'purchase_bill_no', 'purchaseBillNo');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Debit Note No',
      dataIndex: 'debitNoteNo',
      width:     180,
      render: (_v, row) => {
        const v = pickField(row, 'debit_note_no', 'debitNoteNo');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Taxable',
      dataIndex: 'taxableAmount',
      width:     130,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtMoney(pickField(row, 'taxable_amount', 'taxableAmount'))}
        </span>
      )
    },
    {
      title:     'GST',
      dataIndex: 'totalTax',
      width:     120,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtMoney(readGst(row))}
        </span>
      )
    },
    {
      title:     'Grand Total',
      dataIndex: 'grandTotal',
      width:     140,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtMoney(pickField(row, 'grand_total', 'grandTotal'))}
        </span>
      )
    },
    {
      title:     'Inventory Posted',
      dataIndex: 'inventoryPosted',
      width:     140,
      render: (_v, row) => {
        const posted = !!pickField(row, 'inventory_posted', 'inventoryPosted');
        return (
          <Tag
            color={posted ? 'success' : 'default'}
            className="grn-status-tag"
          >
            {posted ? 'Posted' : 'Pending'}
          </Tag>
        );
      }
    },
    {
      title:     'Accounting Voucher',
      dataIndex: 'accountingVoucherNo',
      width:     180,
      render: (_v, row) => {
        const v = pickField(row, 'accounting_voucher_no', 'accountingVoucherNo');
        if (!v) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{v}</span>;
      }
    },
    {
      title:     'Status',
      dataIndex: 'status',
      width:     140,
      render: (_v, row) => {
        const v = pickField(row, 'status', 'status');
        if (!v) return (
          <Tag className="grn-status-tag" color="default">—</Tag>
        );
        return (
          <Tag color={returnStatusColor(v)} className="grn-status-tag">
            {returnStatusLabel(v) || String(v).replace(/_/g, ' ')}
          </Tag>
        );
      }
    }
  ]), [openView]);

  // ── Render ───────────────────────────────────────────────────
  if (!canView && !canViewReports) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view the purchase return register."
          description="Ask your master account to grant erp.purchase.returns.view or erp.purchase.returns.reports.view."
        />
      </div>
    );
  }

  return (
    <div className="grn-shell">
      {/* Header strip */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><FileDoneOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">Purchase Return Register</span>
            <span className="grn-headstrip__subtitle">
              All purchase returns — draft, confirmed, posted, failed and
              cancelled. Backend owns return-no allocation, debit-note
              voucher posting, inventory ledger idempotency, and ITC
              reversal. This surface is read-only.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="grn-stat-chip">
            <strong>{total}</strong> total
          </span>
        </Space>
      </div>

      {/* Sticky toolbar */}
      <div className="grn-toolbar">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Supplier name / GSTIN…"
          value={supplierInput}
          onChange={(e) => setSupplierInput(e.target.value)}
          onPressEnter={(e) => {
            setSupplierInput(e.target.value);
            setSupplier(e.target.value);
            setPage(1);
          }}
          style={{ width: 280 }}
        />
        <RangePicker
          size="small"
          value={dateRange}
          onChange={onDates}
          format={DATE_FMT}
          allowClear
          placeholder={['From', 'To']}
          style={{ width: 240 }}
        />
        <Tooltip title="Clear filters">
          <Button size="small" onClick={onReset}>Clear</Button>
        </Tooltip>
        <div className="grn-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={load}
          />
        </Tooltip>
      </div>

      {/* Grid card */}
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r) =>
              pickField(r, 'id', 'id')
              || pickField(r, 'return_no', 'returnNo')}
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            onRow={(row) => ({
              onDoubleClick: () => openView(row)
            })}
            pagination={{
              current:           page,
              pageSize,
              total,
              showSizeChanger:   true,
              pageSizeOptions:   PAGE_SIZE_OPTIONS,
              showQuickJumper:   total > pageSize,
              size:              'small',
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              }
            }}
            locale={{
              emptyText: loading
                ? ' '
                : 'No purchase returns yet.'
            }}
          />
        </Spin>
      </Card>
    </div>
  );
}
